import React, { useEffect, useState, useCallback } from "react";
import api, { API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge, statusLabel } from "@/components/common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Paperclip, Send, Download, Clock, Lock, MessageSquare, Activity,
  Upload, Ban, RotateCcw, Loader2, Eye, EyeOff, Trash2,
} from "lucide-react";

export default function RequestDialog({ requestId, open, onOpenChange, onChanged }) {
  const { session } = useAuth();
  const [req, setReq] = useState(null);
  const [tab, setTab] = useState("comments");
  const [comment, setComment] = useState("");
  const [internalOnly, setInternalOnly] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [sel, setSel] = useState([]);

  const isInternal = session?.roles?.some((r) => ["partner", "manager", "staff", "tenant_admin"].includes(r));
  const canManage = session?.roles?.some((r) => ["partner", "manager", "tenant_admin"].includes(r));
  const statuses = session?.tenant?.config?.statuses || ["Received", "Awaiting Client", "Submitted", "Complete"];

  const load = useCallback(() => {
    if (!requestId) return;
    api.get(`/m1/requests/${requestId}`).then((r) => setReq(r.data)).catch((e) => toast.error(apiError(e)));
  }, [requestId]);
  useEffect(() => { if (open) { setReq(null); setSel([]); load(); } }, [open, load]);

  const setStatus = async (status) => {
    try { await api.patch(`/m1/requests/${requestId}`, { status }); toast.success(`Marked ${status}`); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const recall = async () => {
    try { await api.post(`/m1/requests/${requestId}/recall`); toast.success("Recalled"); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const markNA = async () => {
    try { await api.patch(`/m1/requests/${requestId}`, { not_applicable: true, status: "Complete" }); toast.success("Marked not applicable"); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const submitToFirm = async () => {
    try { await api.post(`/m1/requests/${requestId}/submit`); toast.success("Submitted to the firm"); load(); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/m1/requests/${requestId}/comments`, { body: comment, internal_only: internalOnly });
      setComment(""); setInternalOnly(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [batch, setBatch] = useState(null);

  const putToS3 = (url, body, contentType, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    if (contentType) xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(e.loaded, e.total); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader("ETag"));
      else reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(body);
  });

  const uploadOne = async (file) => {
    const ct = file.type || "application/octet-stream";
    const { data: init } = await api.post(`/m1/requests/${requestId}/attachments/upload-url`,
      { filename: file.name, content_type: ct, size: file.size });
    if (init.multipart) {
      const total = init.part_count;
      const parts = new Array(total);
      const CONC = 4;
      let nextIdx = 0;
      let uploaded = 0;
      const worker = async () => {
        while (true) {
          const i = nextIdx++;
          if (i >= total) break;
          const start = i * init.part_size;
          const blob = file.slice(start, Math.min(start + init.part_size, file.size));
          const { data: pu } = await api.post(`/m1/attachments/${init.attachment_id}/multipart/part-url`, { part_number: i + 1 });
          let last = 0;
          const etag = await putToS3(pu.url, blob, null, (loaded) => {
            uploaded += loaded - last; last = loaded;
            setProgress(Math.round((uploaded / file.size) * 100));
          });
          parts[i] = { PartNumber: i + 1, ETag: etag };
        }
      };
      await Promise.all(Array.from({ length: Math.min(CONC, total) }, worker));
      await api.post(`/m1/attachments/${init.attachment_id}/multipart/complete`, { parts });
    } else {
      await putToS3(init.upload_url, file, ct, (loaded, total) =>
        setProgress(Math.round((loaded / total) * 100)));
      await api.post(`/m1/attachments/${init.attachment_id}/complete`);
    }
  };

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setBusy(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < files.length; i++) {
      setBatch({ index: i, total: files.length, name: files[i].name });
      setProgress(0);
      try { await uploadOne(files[i]); ok++; }
      catch (e) { fail++; toast.error(`${files[i].name}: ${apiError(e) || e.message}`); }
    }
    setBatch(null); setProgress(0); setBusy(false);
    if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} uploaded${fail ? `, ${fail} failed` : ""}`);
    load(); onChanged?.();
  };
  const download = async (a) => {
    try {
      const { data } = await api.get(`/m1/attachments/${a.id}/download`);
      if (data.url) { window.open(data.url, "_blank", "noopener"); return; }
      const link = document.createElement("a");
      link.href = `data:${data.content_type};base64,${data.content_b64}`;
      link.download = data.filename; link.click();
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteAttachment = async (a) => {
    if (!window.confirm(`Remove "${a.filename}"? You can restore it from Recently deleted.`)) return;
    try {
      await api.delete(`/m1/attachments/${a.id}`);
      toast.success("Moved to Recently deleted"); load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const restoreAttachment = async (a) => {
    try {
      await api.post(`/m1/attachments/${a.id}/restore`);
      toast.success("File restored"); load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleSel = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const downloadZip = async (ids) => {
    try {
      const res = await api.post("/m1/attachments/download-zip", { attachment_ids: ids }, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const link = document.createElement("a"); link.href = url; link.download = "attachments.zip"; link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const bulkDelete = async (ids) => {
    if (!window.confirm(`Remove ${ids.length} file(s)? You can restore them from Recently deleted.`)) return;
    try {
      for (const id of ids) await api.delete(`/m1/attachments/${id}`);
      toast.success(`${ids.length} file(s) moved to Recently deleted`); setSel([]); load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  const canPreview = (a) => a.content_type?.startsWith("image/") || a.content_type === "application/pdf";
  const previewAttachment = async (a) => {
    try {
      const { data } = await api.get(`/m1/attachments/${a.id}/download`);
      const src = data.url || `data:${data.content_type};base64,${data.content_b64}`;
      setPreview({ src, filename: a.filename, type: a.content_type });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[90vw] max-w-[90vw] h-[90vh] max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>{req?.name || "Request details"}</DialogTitle>
          <DialogDescription>Request comments, files and activity</DialogDescription>
        </DialogHeader>
        {!req ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
          <div>
            <div className="border-b border-slate-200 dark:border-slate-800 p-6" style={{ borderTop: "3px solid #4F46E5" }}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={req.status} />
                    {req.category_name && <span className="text-xs font-semibold text-indigo-600">{req.category_name}</span>}
                    {req.file_visibility === "hidden_until_submit" && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600"><Lock className="h-3 w-3" /> Files hidden until submit</span>
                    )}
                  </div>
                  <h2 className="mt-2 font-heading text-xl font-bold" data-testid="request-title">{req.name}</h2>
                  <p className="mt-1 text-sm text-slate-500">{req.description}</p>
                  {req.due_date && <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> Due {new Date(req.due_date).toLocaleDateString()}</div>}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {isInternal ? (
                  <>
                    <Select value={req.status} onValueChange={setStatus}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid="request-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{statusLabel(s, false)}</SelectItem>)}</SelectContent>
                    </Select>
                    {canManage && <Button size="sm" variant="outline" data-testid="request-recall" onClick={recall}><RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Recall</Button>}
                    {canManage && <Button size="sm" variant="outline" data-testid="request-na" onClick={markNA}><Ban className="mr-1.5 h-3.5 w-3.5" /> Not applicable</Button>}
                  </>
                ) : (
                  (req.status === "Awaiting Client" || req.status === "Received") ? (
                    <Button size="sm" data-testid="client-submit" onClick={submitToFirm} className="bg-indigo-600 hover:bg-indigo-700">
                      <Send className="mr-1.5 h-3.5 w-3.5" /> Submit to firm
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-500">
                      {req.status === "Submitted" ? "Submitted — awaiting the firm's review." : "This request is complete."}
                    </span>
                  )
                )}
              </div>

              {req.status_history?.length > 0 && (
                <div className="mt-4" data-testid="status-timeline">
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Status timeline</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {req.status_history.map((h, i) => (
                      <div key={`${h.at}-${i}`} className="flex items-center gap-1.5" data-testid={`timeline-step-${i}`}>
                        {i > 0 && <span className="text-slate-300">→</span>}
                        <span className="inline-flex flex-col rounded-md border border-slate-200 dark:border-slate-700 px-2 py-1 leading-tight">
                          <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">{h.status}</span>
                          <span className="text-[10px] text-slate-400">{h.by_name && h.by_name !== "—" ? h.by_name : "System"} · {new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex border-b border-slate-200 dark:border-slate-800 px-6">
              {[["comments", MessageSquare, "Comments"], ["files", Paperclip, "Files"], ["activity", Activity, "Activity"]].map(([k, Icon, label]) => (
                <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
                  className={`flex items-center gap-1.5 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${tab === k ? "border-indigo-600 text-indigo-600" : "border-transparent text-slate-500"}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>

            <div className="p-6">
              {tab === "comments" && (
                <div className="space-y-4">
                  {req.comments?.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No comments yet.</p>}
                  {req.comments?.map((c) => (
                    <div key={c.id} className="flex gap-3">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${c.is_internal_author ? "bg-indigo-500" : "bg-slate-400"}`}>{c.author_name?.[0]}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{c.author_name}</span>
                          {c.internal_only && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600"><EyeOff className="h-3 w-3" /> INTERNAL</span>}
                          <span className="text-[11px] text-slate-400">{new Date(c.created_at).toLocaleString()}</span>
                        </div>
                        <p className="mt-0.5 text-sm text-slate-700 dark:text-slate-300">{c.body}</p>
                      </div>
                    </div>
                  ))}
                  <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                    <Textarea data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Write a comment…" className="border-0 focus-visible:ring-0 resize-none p-0" />
                    <div className="flex items-center justify-between mt-2">
                      {isInternal ? (
                        <label className="flex items-center gap-2 text-xs text-slate-500"><Checkbox checked={internalOnly} onCheckedChange={setInternalOnly} data-testid="comment-internal" /> Internal only</label>
                      ) : <span />}
                      <Button size="sm" data-testid="comment-send" onClick={addComment} className="bg-indigo-600 hover:bg-indigo-700"><Send className="mr-1.5 h-3.5 w-3.5" /> Send</Button>
                    </div>
                  </div>
                </div>
              )}

              {tab === "files" && (
                <div className="space-y-3">
                  <label
                    className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed py-8 transition-colors ${dragActive ? "border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/20" : "border-slate-300 dark:border-slate-700 hover:border-indigo-400"}`}
                    data-testid="upload-dropzone"
                    onDragOver={(e) => { e.preventDefault(); if (!busy) setDragActive(true); }}
                    onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
                    onDrop={(e) => { e.preventDefault(); setDragActive(false); if (!busy) uploadFiles(e.dataTransfer.files); }}
                  >
                    {busy ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : <Upload className="h-6 w-6 text-slate-400" />}
                    <span className="mt-2 text-sm text-slate-500">
                      {busy
                        ? (batch ? `Uploading ${batch.index + 1}/${batch.total} · ${batch.name} — ${progress}%` : `Uploading… ${progress}%`)
                        : (dragActive ? "Drop files to upload" : "Click to upload or drag & drop files here")}
                    </span>
                    {!busy && <span className="mt-0.5 text-xs text-slate-400">You can select multiple files</span>}
                    <input type="file" multiple className="hidden" data-testid="file-input" disabled={busy} onChange={(e) => uploadFiles(e.target.files)} />
                  </label>
                  {busy && (
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800" data-testid="upload-progress">
                      <div className="h-full bg-indigo-500 transition-all" style={{ width: `${progress}%` }} />
                    </div>
                  )}
                  {req.attachments?.length > 0 && (
                    <div className="flex items-center justify-between rounded-lg bg-slate-50 dark:bg-slate-800/50 px-3 py-2" data-testid="files-bulk-bar">
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-500">
                        <Checkbox
                          checked={sel.length > 0 && req.attachments.every((a) => sel.includes(a.id))}
                          onCheckedChange={() => { const all = req.attachments.map((a) => a.id); setSel(all.every((i) => sel.includes(i)) ? [] : all); }}
                          data-testid="files-select-all" />
                        {sel.length ? `${sel.length} selected` : "Select all"}
                      </label>
                      {sel.length > 0 && (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" data-testid="files-download-zip" onClick={() => downloadZip(sel)}><Download className="mr-1.5 h-3.5 w-3.5" /> Download ZIP</Button>
                          {isInternal && <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" data-testid="files-bulk-delete" onClick={() => bulkDelete(sel)}><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete</Button>}
                        </div>
                      )}
                    </div>
                  )}
                  {req.attachments?.length === 0 && <p className="text-sm text-slate-400 text-center py-2">No files visible.</p>}
                  {req.attachments?.map((a) => (
                    <div key={a.id} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 p-3">
                      <div className="flex items-center gap-3">
                        <Checkbox checked={sel.includes(a.id)} onCheckedChange={() => toggleSel(a.id)} data-testid={`file-select-${a.id}`} />
                        <Paperclip className="h-4 w-4 text-slate-400" />
                        <div>
                          <div className="text-sm font-medium">{a.filename}</div>
                          <div className="text-xs text-slate-400">{a.uploaded_by_name} · {(a.size / 1024).toFixed(1)} KB {a.uploaded_by_internal ? "" : "· client"}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {canPreview(a) && <Button size="icon" variant="ghost" data-testid={`preview-${a.id}`} onClick={() => previewAttachment(a)}><Eye className="h-4 w-4" /></Button>}
                        <Button size="icon" variant="ghost" data-testid={`download-${a.id}`} onClick={() => download(a)}><Download className="h-4 w-4" /></Button>
                        {isInternal && <Button size="icon" variant="ghost" className="text-slate-400 hover:text-red-600" data-testid={`delete-attachment-${a.id}`} onClick={() => deleteAttachment(a)}><Trash2 className="h-4 w-4" /></Button>}
                      </div>
                    </div>
                  ))}
                  {isInternal && req.deleted_attachments?.length > 0 && (
                    <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-800 p-3" data-testid="recently-deleted">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Recently deleted</div>
                      <div className="space-y-2">
                        {req.deleted_attachments.map((a) => (
                          <div key={a.id} className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-2 text-slate-500">
                              <Trash2 className="h-3.5 w-3.5" />
                              <span className="line-through">{a.filename}</span>
                              <span className="text-[11px] text-slate-400">removed by {a.deleted_by_name}</span>
                            </div>
                            <Button size="sm" variant="ghost" className="h-7 text-indigo-600 hover:text-indigo-700" data-testid={`restore-${a.id}`} onClick={() => restoreAttachment(a)}><RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore</Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {tab === "activity" && (
                <div className="space-y-3">
                  {req.activity?.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No activity.</p>}
                  {req.activity?.map((a) => (
                    <div key={a.id} className="flex gap-3 text-sm">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
                      <div><span className="font-medium">{a.actor_name}</span> <span className="text-slate-500">{a.action}</span><div className="text-[11px] text-slate-400">{new Date(a.created_at).toLocaleString()}</div></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="w-[90vw] max-w-[90vw] h-[90vh] max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-slate-200 dark:border-slate-800">
            <DialogTitle className="text-sm font-semibold">{preview?.filename}</DialogTitle>
            <DialogDescription className="sr-only">File preview</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 dark:bg-slate-900 flex items-center justify-center" style={{ height: "72vh" }}>
            {preview?.type?.startsWith("image/")
              ? <img src={preview.src} alt={preview.filename} className="max-h-full max-w-full object-contain" data-testid="preview-image" />
              : <iframe src={preview?.src} title={preview?.filename} className="h-full w-full" data-testid="preview-frame" />}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
